// src/pages/billing/InvoiceCreationPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import CreateInvoiceModal from '../../components/billing/CreateInvoiceModal';
import CreatePurchaseInvoiceModal from '../../components/billing/CreatePurchaseInvoiceModal';
import CreateQuotationModal from '../../components/billing/CreateQuotationModal';
import { useNotification } from '../../contexts/NotificationContext';

const InvoiceCreationPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { showAlert } = useNotification();

    // Get type from URL params, default to 'sales'
    const [invoiceType, setInvoiceType] = useState(searchParams.get('type') || 'sales');

    // Update invoice type when URL changes
    useEffect(() => {
        const type = searchParams.get('type');
        if (type && (type === 'sales' || type === 'purchase' || type === 'quotation')) {
            setInvoiceType(type);
        }
    }, [searchParams]);

    // Handle sales invoice creation
    const handleCreateSalesInvoice = async (createdInvoice) => {
        try {
            showAlert('Sales invoice created successfully', 'success');
            navigate('/billing?tab=sales');
        } catch (error) {
            showAlert('Failed to create sales invoice', 'error');
        }
    };

    // Handle purchase invoice creation
    const handleCreatePurchaseInvoice = async (createdInvoice) => {
        try {
            showAlert('Purchase invoice created successfully', 'success');
            navigate('/billing?tab=purchases');
        } catch (error) {
            showAlert('Failed to create purchase invoice', 'error');
        }
    };

    // Handle quotation creation
    const handleCreateQuotation = async (createdQuotation) => {
        try {
            showAlert('Quotation created successfully', 'success');
            navigate('/billing?tab=quotations');
        } catch (error) {
            showAlert('Failed to create quotation', 'error');
        }
    };

    // Update the handleNavigateToAddProduct function
    const handleNavigateToAddProduct = () => {
        console.log('Navigation function called');
        try {
            navigate('/inventory/add');
            showAlert('Navigating to add product...', 'info');
        } catch (error) {
            console.error('Navigation error:', error);
            showAlert('Navigation failed', 'error');
        }
    };

    // Handle back navigation
    const handleBack = () => {
        navigate('/billing');
    };

    return (
        <DashboardLayout>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <button
                                onClick={handleBack}
                                className="mr-4 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                            >
                                <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                Go to Billing
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">
                                    Create {invoiceType === 'sales' ? 'Sales Invoice' : invoiceType === 'purchase' ? 'Purchase Invoice' : 'Quotation'}
                                </h1>
                                <p className="mt-1 text-sm text-gray-600">
                                    Create a new {invoiceType === 'sales' ? 'sales invoice' : invoiceType === 'purchase' ? 'purchase invoice' : 'quotation'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Invoice Type Selector */}
                <div className="mt-6">
                    <div className="border-b border-gray-200">
                        <nav className="-mb-px flex space-x-8">
                            <button
                                onClick={() => {
                                    setInvoiceType('sales');
                                    navigate('/billing/create?type=sales', { replace: true });
                                }}
                                className={`py-2 px-1 border-b-2 font-medium text-sm cursor-pointer ${invoiceType === 'sales'
                                    ? 'border-orange-500 text-orange-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center">
                                    <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                    </svg>
                                    Sales Invoice
                                </div>
                            </button>
                            <button
                                onClick={() => {
                                    setInvoiceType('purchase');
                                    navigate('/billing/create?type=purchase', { replace: true });
                                }}
                                className={`py-2 px-1 border-b-2 font-medium text-sm cursor-pointer ${invoiceType === 'purchase'
                                    ? 'border-orange-500 text-orange-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center">
                                    <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                                    </svg>
                                    Purchase Invoice
                                </div>
                            </button>
                            <button
                                onClick={() => {
                                    setInvoiceType('quotation');
                                    navigate('/billing/create?type=quotation', { replace: true });
                                }}
                                className={`py-2 px-1 border-b-2 font-medium text-sm cursor-pointer ${invoiceType === 'quotation'
                                    ? 'border-purple-500 text-purple-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center">
                                    <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Quotation
                                </div>
                            </button>
                        </nav>
                    </div>
                </div>

                {/* Invoice Creation Content */}
                <div className="mt-6">
                    {invoiceType === 'sales' ? (
                        <div className="bg-white shadow sm:rounded-md">
                            <div className="px-4 py-5 sm:p-6">
                                <CreateInvoiceModal
                                    onClose={handleBack}
                                    onSubmit={handleCreateSalesInvoice}
                                    onNavigateToAddProduct={handleNavigateToAddProduct}
                                    isModal={false}
                                />
                            </div>
                        </div>
                    ) : invoiceType === 'purchase' ? (
                        <div className="bg-white shadow sm:rounded-md">
                            <div className="px-4 py-5 sm:p-6">
                                <CreatePurchaseInvoiceModal
                                    onClose={handleBack}
                                    onSubmit={handleCreatePurchaseInvoice}
                                    isModal={false}
                                    onNavigateToAddProduct={handleNavigateToAddProduct}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="bg-white shadow sm:rounded-md">
                            <div className="px-4 py-5 sm:p-6">
                                <CreateQuotationModal
                                    onClose={handleBack}
                                    onSubmit={handleCreateQuotation}
                                    onNavigateToAddProduct={handleNavigateToAddProduct}
                                    isModal={false}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
};

export default InvoiceCreationPage;